import { useState, type FormEvent } from "react";
import { apiUrl } from "../../utils/jwt-storage";

/**
 * The receipt-verification form + result panel, shared by the public
 * /verify-receipt page (anonymous, AuthShell chrome) and the in-app
 * footer modal (logged-in users — no reason to leave the shell).
 *
 * ONE field: the code exactly as printed
 * (``<run>-<member>-XXXX-XXXX-XXXX-XXXX``) — run and member ids ride
 * inside the string. A valid code returns the server's frozen figures
 * for side-by-side comparison with the paper.
 */
type VerifyResult = {
  valid: boolean;
  platform?: string;
  organization?: string;
  run?: number;
  period_start?: string;
  period_end?: string;
  status?: string;
  paid_at?: string | null;
  reference?: string;
  amount?: number;
  currency?: string;
};

const inputStyle = {
  width: "100%",
  height: 40,
  boxSizing: "border-box" as const,
  padding: "0 12px",
  borderRadius: 5,
  border: "1px solid var(--color-neutral-border, #cbd5e1)",
  fontSize: "var(--font-size-field-input, 14px)",
  background: "var(--color-neutral-background, #fff)",
  color: "var(--color-neutral-content, inherit)",
};

export const VerifyReceiptForm = ({ initialCode = "" }: { initialCode?: string }) => {
  const [code, setCode] = useState(initialCode);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setResult(null);
    setError(null);
    setSubmitting(true);
    try {
      const qs = new URLSearchParams({ code: code.trim() });
      const res = await fetch(apiUrl(`/api/receipts/verify/?${qs}`), {
        headers: { Accept: "application/json" },
      });
      const body = (await res.json().catch(() => ({}))) as VerifyResult;
      setResult(body);
    } catch {
      setError("Network error. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const row = (label: string, value: string | number | null | undefined) =>
    value != null && value !== "" ? (
      <tr>
        <td style={{ padding: "3px 10px 3px 0", color: "var(--color-neutral-content-subtler, #4b5563)" }}>{label}</td>
        <td style={{ padding: "3px 0", fontWeight: 600 }}>{value}</td>
      </tr>
    ) : null;

  return (
    <form onSubmit={onSubmit}>
      <p
        style={{
          margin: "0 0 10px",
          color: "var(--color-neutral-content-subtler, #4b5563)",
          fontSize: 13.5,
          lineHeight: 1.5,
        }}
      >
        Enter the verification code printed on the payout receipt. A valid code returns the platform's own frozen
        figures — compare them against the document you were given.
      </p>
      <label htmlFor="vr-code" style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
        Verification code
      </label>
      <input
        id="vr-code"
        style={inputStyle}
        placeholder="12-5-AAAA-BBBB-CCCC-DDDD"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        required
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      {error && (
        <p
          style={{
            color: "var(--color-negative-content, #b91c1c)",
            margin: "8px 0",
            fontSize: "var(--font-size-body-small)",
          }}
        >
          {error}
        </p>
      )}
      {result &&
        (result.valid ? (
          <div
            style={{
              margin: "10px 0",
              padding: "12px 14px",
              borderRadius: 8,
              border: "1px solid rgba(31, 122, 90, 0.35)",
              background: "rgba(31, 122, 90, 0.07)",
            }}
          >
            <p style={{ margin: "0 0 6px", fontWeight: 700, color: "var(--color-positive-content, #047857)" }}>
              ✓ Valid — issued by {result.platform}
            </p>
            <table style={{ fontSize: "var(--font-size-body-small)", borderCollapse: "collapse" }}>
              <tbody>
                {row("Organization", result.organization)}
                {row("Run", result.run)}
                {row("Period", `${result.period_start} → ${result.period_end}`)}
                {row("Status", result.status === "paid" ? `Paid ${result.paid_at?.slice(0, 10) ?? ""}` : result.status)}
                {row("Reference", result.reference)}
                {row("Amount", `${result.amount} ${result.currency || ""}`.trim())}
              </tbody>
            </table>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: "var(--font-size-caption)",
                color: "var(--color-neutral-content-subtler, #4b5563)",
              }}
            >
              If any figure on the document differs from the above, the document was altered.
            </p>
          </div>
        ) : (
          <p
            style={{
              margin: "10px 0",
              padding: "12px 14px",
              borderRadius: 8,
              border: "1px solid rgba(192, 53, 58, 0.35)",
              background: "rgba(229, 72, 77, 0.07)",
              color: "var(--color-negative-content, #b91c1c)",
              fontWeight: 600,
            }}
          >
            ✗ Not valid — no matching record. Check the code exactly as printed.
          </p>
        ))}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            height: 36,
            padding: "0 18px",
            borderRadius: 5,
            border: "none",
            background: "var(--color-primary-surface, #4c5fa9)",
            color: "#fff",
            fontWeight: 600,
            cursor: submitting ? "default" : "pointer",
          }}
        >
          {submitting ? "Checking…" : "Verify"}
        </button>
      </div>
    </form>
  );
};
